import { quillClassFromInstance } from '@calendly/rich-text-editor'
import { useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react'

function getTopLeft(originPosition, container, offsetWidth, useNarrowTooltip = false) {
  const TOOLBAR_HEIGHT = 41
  const preferredLeft = originPosition.left + originPosition.width / 2 - offsetWidth / 2

  const left = useNarrowTooltip ? Math.max(-8, Math.min(361 - offsetWidth - 24, preferredLeft)) : preferredLeft

  const top = originPosition.bottom + TOOLBAR_HEIGHT
  return { left, top }
}

const closedTooltipState = {
  linkIndex: null,
  linkLength: null,
  linkURL: '',
  isTooltipOpen: false
}

const linkStateReducer = (state, { type, payload }) => {
  switch (type) {
    case 'linkSelection': {
      const { index, length, url } = payload
      return {
        linkIndex: index,
        linkLength: length,
        linkURL: url,
        isTooltipOpen: true
      }
    }
    case 'closeTooltip':
      return closedTooltipState
    default:
      return state
  }
}

export const useSimpleLinkTooltip = ({ onLinkEdit, useNarrowTooltip = false }) => {
  const editorRef = useRef()
  const [tooltipPosition, setTooltipPosition] = useState({})
  const [tooltipNode, setTooltipNode] = useState(null)

  const [{
    isTooltipOpen,
    linkURL,
    linkIndex,
    linkLength
  }, dispatch] = useReducer(linkStateReducer, closedTooltipState)

  const linkSelection = useMemo(() => {
    if (linkIndex === null || linkLength === null) { return null }

    return {
      index: linkIndex,
      length: linkLength
    }
  }, [linkIndex, linkLength])

  const tooltipOffsetWidth = tooltipNode && tooltipNode.offsetWidth

  useLayoutEffect(() => {
    if (!tooltipOffsetWidth || !linkSelection) {
      return
    }

    const position = editorRef.current.getBounds(linkSelection)

    const pos = getTopLeft(position, editorRef.current.root, tooltipOffsetWidth, useNarrowTooltip)
    setTooltipPosition(pos)
  }, [linkSelection, tooltipOffsetWidth, useNarrowTooltip])

  const onChangeSelection = useCallback((range, source, editor) => {
    if (range == null || source !== 'user') { return }

    editorRef.current = editor

    if (range.length < 2) {
      const Quill = quillClassFromInstance(editor)
      const Link = Quill.import('formats/link')

      const [link, offset] = editor.scroll.descendant(
        Link,
        range.index,
      )
      if (link != null) {
        dispatch({
          type: 'linkSelection',
          payload: {
            index: range.index - offset,
            length: link.length(),
            url: Link.formats(link.domNode)
          }
        })
        return
      }
    }
    dispatch({ type: 'closeTooltip' })
  }, [])

  const onEdit = useCallback(() => {
    onLinkEdit(editorRef.current, linkSelection, linkURL)
    dispatch({ type: 'closeTooltip' })
  }, [linkSelection, linkURL, onLinkEdit])

  const onRemove = useCallback(() => {
    editorRef.current.formatText(linkSelection, 'link', false, 'user')
    dispatch({ type: 'closeTooltip' })
  }, [linkSelection])

  return {
    editorProps: {
      onChangeSelection
    },
    tooltipProps: {
      ref: setTooltipNode,
      isOpen: isTooltipOpen,
      url: linkURL,
      position: tooltipPosition,
      onEdit,
      onRemove
    }
  }
}
